var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/form-config.ts
function defineFormPage(config) {
  return config;
}
__name(defineFormPage, "defineFormPage");

// src/use-form-page.ts
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useStore } from "@tanstack/react-form";
function formatError(err) {
  if (!err) return "Validation error";
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    if ("message" in err && typeof err.message === "string") return err.message;
    if ("error" in err && typeof err.error === "string") return err.error;
    try {
      return JSON.stringify(err);
    } catch (stringifyErr) {
      console.warn("useFormPage: failed to stringify field validation error", stringifyErr);
      return "Validation error";
    }
  }
  return String(err);
}
__name(formatError, "formatError");
function useDraftForm(routeId, value) {
  const key = `draft_form_${routeId ?? "unknown"}`;
  const [hasDraft, setHasDraft] = useState(false);
  useEffect(() => {
    if (routeId && localStorage.getItem(key)) {
      setHasDraft(true);
    }
  }, [
    key,
    routeId
  ]);
  useEffect(() => {
    if (!routeId) return;
    const timer = setTimeout(() => {
      localStorage.setItem(key, JSON.stringify(value));
    }, 500);
    return () => clearTimeout(timer);
  }, [
    key,
    value,
    routeId
  ]);
  const restoreDraft = useCallback(() => {
    if (!routeId) return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (parseErr) {
      console.warn("useFormPage: failed to parse saved draft, discarding it", parseErr);
      return null;
    }
  }, [
    key,
    routeId
  ]);
  const dismissDraft = useCallback(() => setHasDraft(false), []);
  const clearDraft = useCallback(() => {
    if (!routeId) return;
    localStorage.removeItem(key);
    setHasDraft(false);
  }, [
    key,
    routeId
  ]);
  return {
    hasDraft,
    restoreDraft,
    dismissDraft,
    clearDraft
  };
}
__name(useDraftForm, "useDraftForm");
function useFormPage(config, mutation, options) {
  const { routeId, enableDraft = false, enableUnsavedPrompt, onBeforeSubmit, onSuccess, onError } = options ?? {};
  const defaultValues = useMemo(() => {
    if (config.mode === "edit" && options?.initialValues) {
      return {
        ...config.defaultValues,
        ...options.initialValues
      };
    }
    return config.defaultValues;
  }, [
    config.defaultValues,
    config.mode,
    options?.initialValues
  ]);
  const form = useForm({
    defaultValues,
    onSubmit: /* @__PURE__ */ __name(async ({ value }) => {
      try {
        let finalValues = value;
        if (onBeforeSubmit) {
          finalValues = await onBeforeSubmit(value);
        }
        const result = await mutation.mutateAsync(finalValues);
        if (onSuccess) {
          onSuccess(result);
        }
      } catch (err) {
        if (onError && err instanceof Error) {
          onError(err);
        } else {
          throw err;
        }
      }
    }, "onSubmit")
  });
  useEffect(() => {
    form.reset(defaultValues);
  }, [
    defaultValues,
    form
  ]);
  const formValues = useStore(form.store, (s) => s.values);
  const isSubmitting = useStore(form.store, (s) => s.isSubmitting);
  const isDirty = useStore(form.store, (s) => s.isDirty);
  const submissionAttempts = useStore(form.store, (s) => s.submissionAttempts);
  const { hasDraft, restoreDraft, dismissDraft, clearDraft } = useDraftForm(routeId && enableDraft ? routeId : void 0, formValues);
  const fieldError = useCallback((fieldMeta) => {
    if (submissionAttempts === 0 && !fieldMeta?.isBlurred) return void 0;
    const errors = fieldMeta?.errors;
    if (!errors || errors.length === 0) return void 0;
    return formatError(errors[0]);
  }, [
    submissionAttempts
  ]);
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await form.handleSubmit();
    } catch (err) {
      console.error("[useFormPage] form.handleSubmit error:", err);
      if (onError && err instanceof Error) {
        onError(err);
      }
    }
  }, [
    form,
    onError
  ]);
  useEffect(() => {
    if (typeof window !== "undefined" && enableUnsavedPrompt && isDirty) {
      const handler = /* @__PURE__ */ __name((e) => {
        e.preventDefault();
      }, "handler");
      window.addEventListener("beforeunload", handler);
      return () => window.removeEventListener("beforeunload", handler);
    }
  }, [
    enableUnsavedPrompt,
    isDirty
  ]);
  return {
    form,
    isSubmitting,
    isDirty,
    submissionAttempts,
    handleSubmit,
    fieldError,
    hasDraft: Boolean(routeId && enableDraft) ? hasDraft : false,
    restoreDraft,
    clearDraft,
    dismissDraft
  };
}
__name(useFormPage, "useFormPage");

// src/use-form-modal.ts
import { useCallback as useCallback2, useMemo as useMemo2, useState as useState2 } from "react";
import { useForm as useForm2, useStore as useStore2 } from "@tanstack/react-form";
function useFormModal(config, mutation, options) {
  const { onBeforeSubmit, onSuccess, onError } = options ?? {};
  const [submissionAttempts, setSubmissionAttempts] = useState2(0);
  const defaultValues = useMemo2(() => {
    if (options?.initialValues) {
      return {
        ...config.defaultValues,
        ...options.initialValues
      };
    }
    return config.defaultValues;
  }, [
    config.defaultValues,
    options?.initialValues
  ]);
  const form = useForm2({
    defaultValues,
    onSubmit: /* @__PURE__ */ __name(async ({ value }) => {
      let finalValues = value;
      if (onBeforeSubmit) {
        finalValues = await onBeforeSubmit(value);
      }
      const result = await mutation.mutateAsync(finalValues);
      if (onSuccess) onSuccess(result);
    }, "onSubmit")
  });
  const isSubmitting = useStore2(form.store, (s) => s.isSubmitting);
  const fieldError = useCallback2((fieldMeta) => {
    if (submissionAttempts === 0 && !fieldMeta?.isBlurred) return void 0;
    const errors = fieldMeta?.errors;
    if (!errors || errors.length === 0) return void 0;
    const err = errors[0];
    if (!err) return void 0;
    if (typeof err === "string") return err;
    if (typeof err === "object") {
      if ("message" in err && typeof err.message === "string") return err.message;
      if ("error" in err && typeof err.error === "string") return err.error;
      try {
        return JSON.stringify(err);
      } catch (stringifyErr) {
        console.warn("useFormModal: failed to stringify field validation error", stringifyErr);
        return "Validation error";
      }
    }
    return String(err);
  }, [
    submissionAttempts
  ]);
  const handleSubmit = useCallback2(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSubmissionAttempts((n) => n + 1);
    try {
      await form.handleSubmit();
    } catch (err) {
      if (onError && err instanceof Error) {
        onError(err);
      } else {
        throw err;
      }
    } finally {
      form.store.setState((prev) => ({
        ...prev,
        isSubmitting: false
      }));
    }
  }, [
    form,
    onError
  ]);
  return {
    form,
    isSubmitting,
    submissionAttempts,
    handleSubmit,
    fieldError
  };
}
__name(useFormModal, "useFormModal");
export {
  defineFormPage,
  useFormModal,
  useFormPage
};
//# sourceMappingURL=index.js.map